import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Icons from "components/CustomIcons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSlider from "components/MDSlider";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";

function ModalDevice3({ color, device, sendValueServer, addresses, deviceState }) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const is3Byte = device.type === "device4";

  const [tabIndex, setTabIndex] = useState("1");
  const [isOn, setIsOn] = useState(false);
  const [red, setRed] = useState(0);
  const [green, setGreen] = useState(0);
  const [blue, setBlue] = useState(0);
  const [intensity, setIntensity] = useState(0);
  const [white, setWhite] = useState(0);

  useEffect(() => {
    setIsOn(Boolean(addresses.find((a) => a.id === device.onOffStateAddress)?.value));

    if (is3Byte && typeof deviceState === "object" && deviceState !== null) {
      const { r = 0, g = 0, b = 0 } = deviceState;
      setRed(Math.round(r / 2.55));
      setGreen(Math.round(g / 2.55));
      setBlue(Math.round(b / 2.55));
    } else {
      setRed(addresses.find((a) => a.id === device.redValueAddress)?.value ?? 0);
      setGreen(addresses.find((a) => a.id === device.greenValueAddress)?.value ?? 0);
      setBlue(addresses.find((a) => a.id === device.blueValueAddress)?.value ?? 0);
    }

    setIntensity(addresses.find((a) => a.id === device.intensityValueAddress)?.value ?? 0);
    setWhite(addresses.find((a) => a.id === device.whiteValueAddress)?.value ?? 0);
  }, [addresses, device, deviceState, is3Byte]);

  const handleChangeTab = (event, newValue) => setTabIndex(newValue);

  const sendRGB = (channel, value) => {
    if (is3Byte) {
      const r = channel === "r" ? Math.round(value * 2.55) : Math.round(red * 2.55);
      const g = channel === "g" ? Math.round(value * 2.55) : Math.round(green * 2.55);
      const b = channel === "b" ? Math.round(value * 2.55) : Math.round(blue * 2.55);
      sendValueServer(device.valueAddress, {
        id: device.valueAddress,
        value: { r, g, b },
      });
    } else {
      const addressMap = {
        r: device.redValueAddress,
        g: device.greenValueAddress,
        b: device.blueValueAddress,
      };
      sendValueServer(addressMap[channel], {
        id: addressMap[channel],
        value,
      });
    }

    if (channel === "r") setRed(value);
    if (channel === "g") setGreen(value);
    if (channel === "b") setBlue(value);

    if (device.whiteValue) {
      sendValueServer(device.whiteValueAddress, {
        id: device.whiteValueAddress,
        value: 0,
      });
      setWhite(0);
    }
  };

  const handleOnOffToggle = () => {
    const newValue = isOn ? 0 : 1;
    sendValueServer(device.onOffAddress, {
      id: device.onOffAddress,
      value: newValue,
    });
    setIsOn(!isOn);
  };

  const rgbColor =
    white > 0
      ? "rgb(0,0,0)"
      : `rgb(${Math.round(red * 2.55)}, ${Math.round(green * 2.55)}, ${Math.round(blue * 2.55)})`;

  const labelColor = isOn ? "white" : "text";

  return (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <MDBox
        p={3}
        height="100%"
        variant="gradient"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        bgColor={isOn ? color : "white"}
        sx={({ palette: { background } }) => ({
          background: darkMode && !isOn && background.card,
        })}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Icons
            name={isOn ? "LightbulbOnOutline" : "LightbulbOutline"}
            size={40}
            color={darkMode || isOn ? "white" : "black"}
          />
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              backgroundColor: rgbColor,
              marginLeft: 16,
            }}
          />
          <MDBox mt={-0.5} mr={-1.5}>
            <Switch checked={Boolean(isOn)} onChange={handleOnOffToggle} />
          </MDBox>
        </MDBox>

        <TabContext value={tabIndex}>
          <Tabs
            value={tabIndex}
            onChange={handleChangeTab}
            textColor="inherit"
            indicatorColor="secondary"
          >
            <Tab label={t("color").toUpperCase()} value="1" />
            <Tab label={t("intensity").toUpperCase()} value="2" />
            <Tab label={t("whiteL").toUpperCase()} value="3" disabled={!device.whiteValue} />
          </Tabs>

          <TabPanel value="1">
            {[
              { key: "r", label: "R", value: red },
              { key: "g", label: "G", value: green },
              { key: "b", label: "B", value: blue },
            ].map(({ key, label, value }) => (
              <MDBox
                key={key}
                mt={2}
                mx={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color={labelColor}>
                    {label}
                  </MDTypography>
                  <MDTypography variant="h6" color={labelColor}>
                    {value}%
                  </MDTypography>
                </MDBox>
                <MDSlider
                  value={value}
                  onChange={(val) => sendRGB(key, val)}
                  min={0}
                  max={100}
                  color="info"
                />
              </MDBox>
            ))}
          </TabPanel>

          <TabPanel value="2">
            <MDBox mt={3} mx={2} textAlign="center">
              <MDSlider
                value={intensity}
                onChange={(val) => {
                  setIntensity(val);
                  sendValueServer(device.intensityValueAddress, {
                    id: device.intensityValueAddress,
                    value: val,
                  });
                }}
                min={0}
                max={100}
                color="info"
              />
              <MDTypography variant="h4" color={labelColor}>
                {intensity}%
              </MDTypography>
            </MDBox>
          </TabPanel>

          <TabPanel value="3">
            <MDBox mt={3} mx={2} textAlign="center">
              <MDSlider
                value={white}
                onChange={(val) => {
                  setWhite(val);
                  sendValueServer(device.whiteValueAddress, {
                    id: device.whiteValueAddress,
                    value: val,
                  });
                  if (is3Byte) {
                    sendValueServer(device.valueAddress, {
                      id: device.valueAddress,
                      value: { r: 0, g: 0, b: 0 },
                    });
                  } else {
                    sendValueServer(device.redValueAddress, {
                      id: device.redValueAddress,
                      value: 0,
                    });
                    sendValueServer(device.greenValueAddress, {
                      id: device.greenValueAddress,
                      value: 0,
                    });
                    sendValueServer(device.blueValueAddress, {
                      id: device.blueValueAddress,
                      value: 0,
                    });
                  }
                }}
                min={0}
                max={100}
                color="info"
              />
              <MDTypography variant="h4" color={labelColor}>
                {white}%
              </MDTypography>
            </MDBox>
          </TabPanel>
        </TabContext>

        <MDBox mt={2} lineHeight={1} textAlign="center">
          <MDTypography variant="body2" color={labelColor} textTransform="capitalize">
            {device?.name?.toUpperCase()}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}

ModalDevice3.defaultProps = {
  color: "info",
  device: {},
  addresses: [],
  deviceState: {},
};

ModalDevice3.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  device: PropTypes.object,
  sendValueServer: PropTypes.func.isRequired,
  addresses: PropTypes.array,
  deviceState: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
};

export default ModalDevice3;
